import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FolderPlus, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, STATUS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Empty, ErrorState, Loading, PageHeader } from "@/components/States";
import ProjectCard from "@/components/ProjectCard";
import ProjectForm from "@/components/ProjectForm";

export default function Projects() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { data, error, loading, reload } = useFetch("/projects");
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const isAdmin = user.role === "admin";

  const create = async (payload) => {
    try {
      const { data: p } = await api.post("/projects", payload);
      toast.success("Project created"); setOpen(false); nav(`/projects/${p.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const list = (data || []).filter((p) => (status === "all" || p.status === status) &&
    `${p.name} ${p.client_name} ${p.address}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-6 md:p-10 lg:p-12">
      <PageHeader eyebrow={isAdmin ? "Every job in the workspace" : "Assigned to you"} title="Projects">
        {isAdmin && <Button className="rounded-full" onClick={() => setOpen(true)} data-testid="new-project-button"><Plus className="mr-1 h-4 w-4" />New project</Button>}
      </PageHeader>
      <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search projects" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="project-search-input" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[["all", "All"], ...Object.entries(STATUS)].map(([k, v]) => (
            <button key={k} onClick={() => setStatus(k)} data-testid={`filter-status-${k}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${status === k ? "border-primary bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>{v}</button>
          ))}
        </div>
      </div>
      {loading ? <Loading rows={3} /> : error ? <ErrorState message={error} onRetry={reload} /> : list.length === 0 ? (
        <Empty icon={FolderPlus} title={data.length ? "No matching projects" : "No projects yet"}
          text={isAdmin ? "Create your first project to start a planning board and estimate." : "When the studio assigns you to a project, it will show up here."}
          action={isAdmin && !data.length && <Button className="rounded-full" onClick={() => setOpen(true)} data-testid="empty-new-project-button">Create a project</Button>} />
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="projects-grid">
          {list.map((p, i) => <ProjectCard key={p.id} p={p} i={i} showBudget={isAdmin} />)}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle className="section-title font-normal">New project</DialogTitle></DialogHeader>
          <ProjectForm onSubmit={create} submitLabel="Create project" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
